import React, { useState, useEffect, useRef, useCallback } from "react";
import DonutLabelingImageViewer from "./DonutLabelingImageViewer";
import DonutLabelingFieldGrid from "./DonutLabelingFieldGrid";
import DonutLabelingAiPanel from "./DonutLabelingAiPanel";
import DonutLabelingSimilarPanel from "./DonutLabelingSimilarPanel";
import DonutLabelingSettings, {
  DEFAULT_SETTINGS,
  loadSettings,
  saveSettings,
} from "./DonutLabelingSettings";

/**
 * DonutLabelingMode — Task 1.3 skeleton
 *
 * Props:
 *   sampleIds : string[]    — ordered list of sample IDs to review
 *   onExit    : () => void  — called on Escape or after last sample
 *   onSaved   : (id) => void
 *
 * Layout: 2-column grid (image left / field inputs right)
 * Keyboard: Escape → onExit, Ctrl/Cmd+Enter → saveAndNext
 *
 * Task 1.4 will replace the inline <img> with <DonutLabelingImageViewer>.
 * Task 1.5 will replace the simple <input> list with <DonutLabelingFieldGrid>.
 */
export default function DonutLabelingMode({ sampleIds, onExit, onSaved }) {
  const [idx, setIdx] = useState(0);
  const [sample, setSample] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Settings — loaded from localStorage on mount
  const [settings, setSettings] = useState(() => loadSettings());
  const [showSettings, setShowSettings] = useState(false);

  // AI ensemble state
  const [ensemble, setEnsemble] = useState(null);
  const [ensembleLoading, setEnsembleLoading] = useState(false);
  const extractAbortRef = useRef(null); // AbortController for inflight extract

  // Similar GT state
  const [similar, setSimilar] = useState({
    by_car_name: [],
    by_vin_suffix: [],
  });

  const total = sampleIds.length;
  const id = sampleIds[idx];

  // ── Auto-extract: runs after sample loads ───────────────────────────────
  const runExtract = useCallback(async (currentSample, abortSignal) => {
    if (!currentSample?.image_proxy_url) return;
    setEnsembleLoading(true);
    setEnsemble(null);
    try {
      const imgResp = await fetch(currentSample.image_proxy_url, {
        signal: abortSignal,
      });
      if (!imgResp.ok) throw new Error(`image fetch HTTP ${imgResp.status}`);
      const blob = await imgResp.blob();

      const fd = new FormData();
      fd.append("file", blob, "image.jpg");
      fd.append("sample_id", currentSample.id);

      const extractResp = await fetch("/api/donut/labeling/extract", {
        method: "POST",
        body: fd,
        signal: abortSignal,
      });
      if (!extractResp.ok)
        throw new Error(`extract HTTP ${extractResp.status}`);
      const result = await extractResp.json();
      setEnsemble(result);
    } catch (e) {
      if (e.name === "AbortError") return; // cancelled by sample change
      setEnsemble({
        donut: null,
        gemini: null,
        consensus: {},
        donut_error: e.message,
        gemini_error: e.message,
        donut_ms: null,
        gemini_ms: null,
      });
    } finally {
      setEnsembleLoading(false);
    }
  }, []);

  // Fetch sample metadata + GT when idx changes
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    // Cancel any inflight extract from previous sample
    if (extractAbortRef.current) {
      extractAbortRef.current.abort();
    }
    setEnsemble(null);
    setEnsembleLoading(false);

    fetch(`/api/donut/labeling/sample/${encodeURIComponent(id)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((s) => {
        setSample(s);
        setFields(s.gt || {});
        // Kick off AI extract unless auto_extract is disabled in settings
        if (settings.auto_extract) {
          const ctrl = new AbortController();
          extractAbortRef.current = ctrl;
          runExtract(s, ctrl.signal);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    // Prefetch next image so it is in browser cache when we advance
    const nextId = sampleIds[idx + 1];
    if (nextId) {
      const img = new Image();
      img.src = `/api/donut/labeling/image/${encodeURIComponent(nextId)}`;
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (extractAbortRef.current) extractAbortRef.current.abort();
    };
  }, []);

  // Debounced similar GT lookup — fires 400ms after car_name or vin changes
  useEffect(() => {
    const t = setTimeout(() => {
      fetch("/api/donut/labeling/similar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ car_name: fields.car_name, vin: fields.vin }),
      })
        .then((r) => r.json())
        .then(setSimilar)
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [fields.car_name, fields.vin]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveAndNext = async () => {
    if (!sample || saving) return;
    setSaving(true);
    try {
      await fetch("/api/donut/labeling/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rows: [{ sample_id: id, doc_type: sample.doc_type, gt: fields }],
        }),
      });
      onSaved && onSaved(id);
      if (idx < total - 1) {
        setIdx(idx + 1);
      } else {
        onExit();
      }
    } catch (e) {
      alert(`저장 실패: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const goPrev = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        onExit();
      } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        saveAndNext();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [sample, fields, idx, saving]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── render ──────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#fff",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "8px 16px",
          borderBottom: "1px solid #e5e7eb",
          background: "#f9fafb",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>
          라벨링 모드
        </span>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {idx + 1} / {total}
        </span>
        {sample && (
          <>
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 4,
                background: "#dbeafe",
                color: "#1d4ed8",
                fontFamily: "monospace",
              }}
            >
              {sample.id}
            </span>
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 4,
                background: "#f3f4f6",
                color: "#374151",
              }}
            >
              {sample.doc_type} · {sample.split}
            </span>
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 4,
                background:
                  sample.status === "confirmed" ? "#dcfce7" : "#fef9c3",
                color: sample.status === "confirmed" ? "#166534" : "#713f12",
              }}
            >
              {sample.status || "pending"}
            </span>
          </>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            onClick={goPrev}
            disabled={idx === 0 || loading || saving}
            style={{
              fontSize: 12,
              padding: "4px 12px",
              borderRadius: 4,
              border: "1px solid #d1d5db",
              background: "#fff",
              cursor: idx === 0 ? "not-allowed" : "pointer",
              opacity: idx === 0 ? 0.4 : 1,
            }}
          >
            이전
          </button>
          <button
            onClick={saveAndNext}
            disabled={loading || saving || !sample}
            style={{
              fontSize: 12,
              padding: "4px 14px",
              borderRadius: 4,
              border: "none",
              background: saving || !sample ? "#93c5fd" : "#2563eb",
              color: "#fff",
              cursor: loading || saving || !sample ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {saving ? "저장중..." : "저장 + 다음 (Ctrl+Enter)"}
          </button>
          <button
            onClick={() => setShowSettings(true)}
            style={{
              fontSize: 12,
              padding: "4px 12px",
              borderRadius: 4,
              border: "1px solid #d1d5db",
              background: showSettings ? "#eff6ff" : "#fff",
              cursor: "pointer",
              color: "#374151",
            }}
          >
            ⚙ 설정
          </button>
          <button
            onClick={onExit}
            style={{
              fontSize: 12,
              padding: "4px 12px",
              borderRadius: 4,
              border: "1px solid #d1d5db",
              background: "#fff",
              cursor: "pointer",
              color: "#374151",
            }}
          >
            나가기 (Esc)
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: "#e5e7eb", flexShrink: 0 }}>
        <div
          style={{
            height: "100%",
            background: "#2563eb",
            width: `${((idx + 1) / total) * 100}%`,
            transition: "width 0.2s",
          }}
        />
      </div>

      {/* Body */}
      {loading ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6b7280",
            fontSize: 14,
          }}
        >
          로딩 중...
        </div>
      ) : error ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#dc2626",
            fontSize: 14,
          }}
        >
          오류: {error}
        </div>
      ) : !sample ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
            fontSize: 14,
          }}
        >
          샘플 없음
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Left column — image viewer */}
          <div
            style={{
              overflow: "hidden",
              borderRight: "1px solid #e5e7eb",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <DonutLabelingImageViewer sample={sample} />
          </div>

          {/* Right column — 18-field input grid + AI ensemble panel */}
          <div
            style={{
              overflow: "auto",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <DonutLabelingFieldGrid
              docType={sample.doc_type}
              fields={fields}
              onChange={setFields}
              onSaveNext={saveAndNext}
              highlightIso={settings.highlight.iso}
            />
            <DonutLabelingAiPanel
              ensemble={ensemble}
              ensembleLoading={ensembleLoading}
              onAdopt={(field, value) =>
                setFields((prev) => ({ ...prev, [field]: value }))
              }
              onReExtract={() => {
                if (extractAbortRef.current) extractAbortRef.current.abort();
                const ctrl = new AbortController();
                extractAbortRef.current = ctrl;
                runExtract(sample, ctrl.signal);
              }}
              highlightDiff={settings.highlight.ensemble_diff}
            />
            <DonutLabelingSimilarPanel
              similar={similar}
              hasCriteria={!!(fields.car_name || fields.vin)}
              onCopyField={(field, value) =>
                setFields((prev) => ({ ...prev, [field]: value }))
              }
            />
          </div>
        </div>
      )}

      {/* Settings drawer — rendered over the labeling UI */}
      {showSettings && (
        <DonutLabelingSettings
          settings={settings}
          onChange={(next) => {
            setSettings(next);
            saveSettings(next);
          }}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
